import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";

const clipboardSlotNumbers = [1, 2, 3, 4, 5];
const minClipboardOverlayLeft = 260;

const normalizeSubCode = (subCode) => {
  if (subCode === null || subCode === undefined || subCode === "") {
    return "98";
  }

  return String(subCode);
};

const createEmptyClipboardSlots = () =>
  clipboardSlotNumbers.map((slotNumber) => ({
    slotNumber,
    customerManualInvoiceClipboardSlotId: null,
    countryCode: null,
    supplierId: null,
    categoryCode: "",
    productSubCode: "",
    lastUpdated: null,
    lastUpdatedBy: "",
    hasValues: false,
  }));

const CustomerClipboardOverlay = ({
  open,
  entityId,
  countryData = [],
  productData = [],
  supplierData = [],
  getFormValues,
  onApply,
  onNotify,
  selectionRequiredMessage = "Select an invoice before using clipboard.",
}) => {
  const [customerClipboardSlots, setCustomerClipboardSlots] = useState(createEmptyClipboardSlots);
  const [loadingClipboardSlots, setLoadingClipboardSlots] = useState(false);
  const [clipboardOverlayPosition, setClipboardOverlayPosition] = useState({ top: 12, left: minClipboardOverlayLeft });
  const [isClipboardOverlayDragging, setIsClipboardOverlayDragging] = useState(false);
  const [isClipboardOverlayMinimized, setIsClipboardOverlayMinimized] = useState(false);
  const clipboardOverlayDragOffsetRef = useRef({ x: 0, y: 0 });

  const normalizeClipboardSlots = useCallback((rawSlots) => {
    const slotsByNumber = new Map();
    (rawSlots || []).forEach((slot) => {
      if (!slot || !clipboardSlotNumbers.includes(Number(slot.slotNumber))) {
        return;
      }

      const normalizedSlotNumber = Number(slot.slotNumber);
      const normalizedCategoryCode = String(slot.categoryCode || "").trim();
      const normalizedProductSubCode = String(slot.productSubCode || "").trim();
      slotsByNumber.set(normalizedSlotNumber, {
        slotNumber: normalizedSlotNumber,
        customerManualInvoiceClipboardSlotId: slot.customerManualInvoiceClipboardSlotId || null,
        countryCode: slot.countryCode || null,
        supplierId: slot.supplierId || null,
        categoryCode: normalizedCategoryCode,
        productSubCode: normalizedProductSubCode,
        lastUpdated: slot.lastUpdated || null,
        lastUpdatedBy: slot.lastUpdatedBy || "",
      });
    });

    return clipboardSlotNumbers.map((slotNumber) => {
      const existingSlot = slotsByNumber.get(slotNumber);
      if (!existingSlot) {
        return {
          slotNumber,
          customerManualInvoiceClipboardSlotId: null,
          countryCode: null,
          supplierId: null,
          categoryCode: "",
          productSubCode: "",
          lastUpdated: null,
          lastUpdatedBy: "",
          hasValues: false,
        };
      }

      const hasValues = Boolean(
        existingSlot.countryCode || existingSlot.supplierId || existingSlot.categoryCode || existingSlot.productSubCode
      );

      return {
        ...existingSlot,
        hasValues,
      };
    });
  }, []);

  const loadCustomerClipboardSlots = useCallback(
    async (nextEntityId) => {
      if (!nextEntityId) {
        setCustomerClipboardSlots(createEmptyClipboardSlots());
        return;
      }

      setLoadingClipboardSlots(true);
      try {
        const response = await axios.get(`/api/v1/entities/${nextEntityId}/manualInvoiceClipboardSlots`);
        setCustomerClipboardSlots(normalizeClipboardSlots(response.data));
      } catch (error) {
        setCustomerClipboardSlots(createEmptyClipboardSlots());
        onNotify?.("error", "Unable to load customer clipboards.");
      } finally {
        setLoadingClipboardSlots(false);
      }
    },
    [normalizeClipboardSlots, onNotify]
  );

  useEffect(() => {
    if (!open) {
      setCustomerClipboardSlots(createEmptyClipboardSlots());
      setLoadingClipboardSlots(false);
      return;
    }

    void loadCustomerClipboardSlots(entityId);
  }, [entityId, loadCustomerClipboardSlots, open]);

  useEffect(() => {
    if (!isClipboardOverlayDragging) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const overlayWidth = 760;
      const minPadding = 8;
      const minLeft = Math.max(minPadding, minClipboardOverlayLeft);
      const nextLeftRaw = event.clientX - clipboardOverlayDragOffsetRef.current.x;
      const nextTopRaw = event.clientY - clipboardOverlayDragOffsetRef.current.y;
      const maxLeft = Math.max(minPadding, window.innerWidth - overlayWidth - minPadding);

      setClipboardOverlayPosition({
        left: Math.min(maxLeft, Math.max(minLeft, nextLeftRaw)),
        top: Math.max(minPadding, nextTopRaw),
      });
    };

    const handleMouseUp = () => {
      setIsClipboardOverlayDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isClipboardOverlayDragging]);

  const handleStartDraggingClipboardOverlay = (event) => {
    if (isClipboardOverlayMinimized || event.button !== 0) {
      return;
    }

    const overlayElement = event.currentTarget.parentElement;
    if (!overlayElement) {
      return;
    }

    const overlayRect = overlayElement.getBoundingClientRect();
    clipboardOverlayDragOffsetRef.current = {
      x: event.clientX - overlayRect.left,
      y: event.clientY - overlayRect.top,
    };
    setIsClipboardOverlayDragging(true);
    event.preventDefault();
  };

  const getClipboardCountryLabel = useCallback(
    (countryCode) => {
      if (!countryCode) {
        return "";
      }

      return countryData.find((item) => Number(item.countryId) === Number(countryCode))?.countryCode || String(countryCode);
    },
    [countryData]
  );

  const getClipboardSupplierLabel = useCallback(
    (supplierId) => {
      if (!supplierId) {
        return "";
      }

      return supplierData.find((item) => Number(item.supplierId) === Number(supplierId))?.supplierName || String(supplierId);
    },
    [supplierData]
  );

  const getClipboardProductTypeLabel = useCallback(
    (countryCode, categoryCode) => {
      const normalizedCategoryCode = String(categoryCode || "").trim();
      if (!normalizedCategoryCode) {
        return "-";
      }

      const matchingProductType = productData.find(
        (item) =>
          Number(item.countryCode) === Number(countryCode) && String(item.categoryCode) === normalizedCategoryCode
      );
      const productTypeDescription =
        matchingProductType?.productShortDescription || matchingProductType?.productLongDescription || normalizedCategoryCode;

      return `${normalizedCategoryCode} - ${productTypeDescription}`;
    },
    [productData]
  );

  const getClipboardProductSubCodeLabel = useCallback(
    (countryCode, categoryCode, productSubCode) => {
      const normalizedCategoryCode = String(categoryCode || "").trim();
      if (!normalizedCategoryCode) {
        return "-";
      }

      const normalizedSubCode = normalizeSubCode(productSubCode);
      if (normalizedSubCode === "98") {
        return "98. N/A";
      }

      const matchingSubCode = productData.find(
        (item) =>
          Number(item.countryCode) === Number(countryCode) &&
          String(item.categoryCode) === normalizedCategoryCode &&
          normalizeSubCode(item.subCode) === normalizedSubCode
      );
      const subCodeDescription =
        matchingSubCode?.productLongDescription || matchingSubCode?.productShortDescription || normalizedSubCode;

      return `${normalizedSubCode} - ${subCodeDescription}`;
    },
    [productData]
  );

  const applyClipboardSlotByNumber = useCallback(
    (slotNumber) => {
      if (!open) {
        onNotify?.("warning", selectionRequiredMessage);
        return;
      }

      const slot = customerClipboardSlots.find((item) => item.slotNumber === slotNumber);
      if (!slot || !slot.hasValues) {
        onNotify?.("warning", `Clipboard ${slotNumber} is empty.`);
        return;
      }

      const slotCountryCode = slot.countryCode || "";
      const normalizedSubCode = slot.categoryCode ? normalizeSubCode(slot.productSubCode) : "";
      const matchingProduct =
        slotCountryCode && slot.categoryCode
          ? productData.find(
              (item) =>
                Number(item.countryCode) === Number(slotCountryCode) &&
                String(item.categoryCode) === String(slot.categoryCode) &&
                normalizeSubCode(item.subCode) === normalizedSubCode
            )
          : null;
      const matchingCountry = countryData.find((item) => Number(item.countryId) === Number(slotCountryCode));
      const matchingSupplier = supplierData.find((item) => Number(item.supplierId) === Number(slot.supplierId));
      const isSupplierInCountry =
        !matchingSupplier || !slotCountryCode || Number(matchingSupplier.countryCode) === Number(slotCountryCode);

      onApply?.({
        countryCode: slotCountryCode,
        supplierId: isSupplierInCountry && slot.supplierId ? slot.supplierId : "",
        claimProductTypeCode: slot.categoryCode || "",
        claimProductSubCode: slot.categoryCode ? normalizedSubCode : "",
        claimProductTypeId: matchingProduct?.productTypeId || "",
        currencyId: matchingCountry?.currency || "",
      });

      onNotify?.("success", `Clipboard ${slotNumber} pasted.`);
    },
    [
      countryData,
      customerClipboardSlots,
      onApply,
      onNotify,
      open,
      productData,
      selectionRequiredMessage,
      supplierData,
    ]
  );

  const saveClipboardSlotByNumber = useCallback(
    async (slotNumber) => {
      if (!open) {
        onNotify?.("warning", selectionRequiredMessage);
        return;
      }

      if (!entityId) {
        onNotify?.("warning", "A valid customer is required to save clipboards.");
        return;
      }

      const formValues = getFormValues?.() || {};

      try {
        const payload = {
          countryCode: formValues.countryCode || null,
          supplierId: formValues.supplierId || null,
          categoryCode: formValues.claimProductTypeCode || null,
          productSubCode: formValues.claimProductTypeCode ? normalizeSubCode(formValues.claimProductSubCode) : null,
        };
        const response = await axios.put(
          `/api/v1/entities/${entityId}/manualInvoiceClipboardSlots/${slotNumber}`,
          payload
        );
        const normalizedUpdatedSlot = normalizeClipboardSlots([response.data]).find((slot) => slot.slotNumber === slotNumber);
        if (!normalizedUpdatedSlot) {
          throw new Error("Invalid clipboard response.");
        }

        setCustomerClipboardSlots((prevSlots) =>
          prevSlots.map((slot) => (slot.slotNumber === slotNumber ? normalizedUpdatedSlot : slot))
        );
        onNotify?.("success", `Clipboard ${slotNumber} saved.`);
      } catch (error) {
        onNotify?.("error", `Failed to save clipboard ${slotNumber}.`);
      }
    },
    [entityId, getFormValues, normalizeClipboardSlots, onNotify, open, selectionRequiredMessage]
  );

  const clearClipboardSlotByNumber = useCallback(
    async (slotNumber) => {
      if (!open) {
        onNotify?.("warning", selectionRequiredMessage);
        return;
      }

      if (!entityId) {
        onNotify?.("warning", "A valid customer is required to clear clipboards.");
        return;
      }

      try {
        const response = await axios.put(`/api/v1/entities/${entityId}/manualInvoiceClipboardSlots/${slotNumber}`, {
          countryCode: null,
          supplierId: null,
          categoryCode: null,
          productSubCode: null,
        });
        const normalizedUpdatedSlot = normalizeClipboardSlots([response.data]).find((slot) => slot.slotNumber === slotNumber);
        if (!normalizedUpdatedSlot) {
          throw new Error("Invalid clipboard response.");
        }

        setCustomerClipboardSlots((prevSlots) =>
          prevSlots.map((slot) => (slot.slotNumber === slotNumber ? normalizedUpdatedSlot : slot))
        );
        onNotify?.("success", `Clipboard ${slotNumber} cleared.`);
      } catch (error) {
        onNotify?.("error", `Failed to clear clipboard ${slotNumber}.`);
      }
    },
    [entityId, normalizeClipboardSlots, onNotify, open, selectionRequiredMessage]
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const hasModifierConflict = event.altKey || event.metaKey;
      const shortcutSlotByKey = Number.parseInt(event.key, 10);
      const shortcutSlotByCode = Number.parseInt(String(event.code || "").replace(/^(Digit|Numpad)/, ""), 10);
      const shortcutSlot = Number.isInteger(shortcutSlotByKey)
        ? shortcutSlotByKey
        : Number.isInteger(shortcutSlotByCode)
          ? shortcutSlotByCode
          : null;
      const isClipboardShortcut =
        event.ctrlKey && !hasModifierConflict && shortcutSlot !== null && clipboardSlotNumbers.includes(shortcutSlot);

      if (!isClipboardShortcut) {
        return;
      }

      event.preventDefault();
      if (event.shiftKey) {
        void saveClipboardSlotByNumber(shortcutSlot);
        return;
      }

      applyClipboardSlotByNumber(shortcutSlot);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [applyClipboardSlotByNumber, open, saveClipboardSlotByNumber]);

  if (!open) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: isClipboardOverlayMinimized ? "calc(100vh - 56px)" : clipboardOverlayPosition.top,
        left: clipboardOverlayPosition.left,
        zIndex: 1401,
        width: isClipboardOverlayMinimized ? 320 : 760,
        maxHeight: isClipboardOverlayMinimized ? "none" : "60vh",
        overflowY: isClipboardOverlayMinimized ? "hidden" : "auto",
        border: "1px solid",
        borderColor: "primary.main",
        borderLeftWidth: 8,
        borderRadius: 1.5,
        backgroundColor: "background.paper",
        boxShadow: isClipboardOverlayMinimized ? 10 : 6,
        opacity: isClipboardOverlayMinimized ? 0.97 : 1,
        transition: "top 240ms ease, width 240ms ease, box-shadow 240ms ease, opacity 240ms ease",
        px: 1.5,
        py: 0.75,
      }}
    >
      <Box
        onMouseDown={handleStartDraggingClipboardOverlay}
        sx={{
          cursor: isClipboardOverlayMinimized ? "default" : isClipboardOverlayDragging ? "grabbing" : "grab",
          userSelect: "none",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: isClipboardOverlayMinimized ? 0 : 0.5,
          mx: -1.5,
          mt: -0.75,
          px: 1.5,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Customer Clipboards
        </Typography>
        <IconButton
          size="small"
          aria-label={isClipboardOverlayMinimized ? "Maximize customer clipboards" : "Minimize customer clipboards"}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            setIsClipboardOverlayMinimized((prevValue) => !prevValue);
          }}
        >
          {isClipboardOverlayMinimized ? <CropSquareIcon fontSize="small" /> : <HorizontalRuleIcon fontSize="small" />}
        </IconButton>
      </Box>
      {!isClipboardOverlayMinimized &&
        (loadingClipboardSlots ? (
          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 0.25,
            }}
          >
            {customerClipboardSlots.map((slot) => (
              <Paper
                key={`clipboard-slot-${slot.slotNumber}`}
                variant="outlined"
                role="button"
                tabIndex={slot.hasValues ? 0 : -1}
                aria-label={`Paste clipboard ${slot.slotNumber}`}
                onClick={() => {
                  if (!slot.hasValues) {
                    return;
                  }

                  applyClipboardSlotByNumber(slot.slotNumber);
                }}
                onKeyDown={(event) => {
                  if (!slot.hasValues) {
                    return;
                  }

                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    applyClipboardSlotByNumber(slot.slotNumber);
                  }
                }}
                sx={{
                  p: 0.35,
                  borderColor: slot.hasValues ? "primary.light" : "divider",
                  backgroundColor: slot.hasValues ? "rgba(25, 118, 210, 0.05)" : "background.paper",
                  cursor: slot.hasValues ? "pointer" : "default",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.15 }}
                  >
                    {slot.hasValues
                      ? `${slot.slotNumber}: ${getClipboardCountryLabel(slot.countryCode) || "-"}, ${getClipboardSupplierLabel(slot.supplierId) || "-"}, ${getClipboardProductTypeLabel(slot.countryCode, slot.categoryCode)}, ${getClipboardProductSubCodeLabel(slot.countryCode, slot.categoryCode, slot.productSubCode)}`
                      : `${slot.slotNumber}: Empty slot`}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 0, px: 0.5 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        void saveClipboardSlotByNumber(slot.slotNumber);
                      }}
                    >
                      <SaveOutlinedIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 0, px: 0.5 }}
                      disabled={!slot.hasValues}
                      onClick={(event) => {
                        event.stopPropagation();
                        applyClipboardSlotByNumber(slot.slotNumber);
                      }}
                    >
                      <ContentPasteIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={!slot.hasValues}
                      sx={{ minWidth: 0, px: 0.5 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        void clearClipboardSlotByNumber(slot.slotNumber);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ))}
    </Box>
  );
};

export default CustomerClipboardOverlay;
